import { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Select,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Divider,
  Tag,
  Switch,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  DeleteOutlined,
  HolderOutlined,
  // All icons
  FontSizeOutlined, AlignLeftOutlined, EditOutlined, MailOutlined,
  PhoneOutlined, LinkOutlined, LockOutlined, NumberOutlined,
  CalculatorOutlined, DollarOutlined, SlidersFilled, CalendarOutlined,
  ClockCircleOutlined, FieldTimeOutlined, DownOutlined, SelectOutlined,
  CheckCircleOutlined, CheckSquareOutlined, BulbOutlined, StarOutlined,
  BgColorsOutlined, UploadOutlined, PictureOutlined, EnvironmentOutlined,
  GlobalOutlined, LineOutlined, CodeOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormField, FIELD_TYPES } from '../../components/FormBuilder/types';

const { Title, Text } = Typography;

const FIELD_ICONS: Record<string, any> = {
  text: <FontSizeOutlined />, textarea: <AlignLeftOutlined />, richtext: <EditOutlined />,
  email: <MailOutlined />, phone: <PhoneOutlined />, url: <LinkOutlined />,
  password: <LockOutlined />, number: <NumberOutlined />, decimal: <CalculatorOutlined />,
  currency: <DollarOutlined />, slider: <SlidersFilled />, date: <CalendarOutlined />,
  time: <ClockCircleOutlined />, datetime: <FieldTimeOutlined />, daterange: <CalendarOutlined />,
  select: <DownOutlined />, multiselect: <SelectOutlined />, radio: <CheckCircleOutlined />,
  checkbox: <CheckSquareOutlined />, switch: <BulbOutlined />, rating: <StarOutlined />,
  color: <BgColorsOutlined />, file: <UploadOutlined />, image: <PictureOutlined />,
  signature: <EditOutlined />, location: <EnvironmentOutlined />, country: <GlobalOutlined />,
  state: <EnvironmentOutlined />, heading: <FontSizeOutlined />, divider: <LineOutlined />,
  html: <CodeOutlined />,
};

function DraggableFieldType({ fieldType }: { fieldType: any }) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('fieldType', fieldType.type);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      style={{
        padding: '8px 10px',
        background: '#fff',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        cursor: 'move',
        marginBottom: 6,
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#1890ff';
        e.currentTarget.style.background = '#f0f8ff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#d9d9d9';
        e.currentTarget.style.background = '#fff';
      }}
    >
      <Space size="small">
        <span style={{ color: '#1890ff', fontSize: 14 }}>
          {FIELD_ICONS[fieldType.type] || <EditOutlined />}
        </span>
        <div>
          <div style={{ fontWeight: 500, fontSize: 12 }}>{fieldType.label}</div>
          <div style={{ fontSize: 10, color: '#888' }}>{fieldType.description}</div>
        </div>
      </Space>
    </div>
  );
}

function SortableFieldItem({
  field,
  isSelected,
  onSelect,
  onDelete,
  columns,
}: {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  columns: number;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldType = FIELD_TYPES.find((ft) => ft.type === field.type);
  const widthPercentage = ((field.position.colSpan || 1) / columns) * 100;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        width: `${widthPercentage}%`,
        padding: '0 4px',
        marginBottom: 8,
        boxSizing: 'border-box',
        display: 'inline-block',
        verticalAlign: 'top',
      }}
    >
      <div
        onClick={onSelect}
        style={{
          background: isSelected ? '#e6f7ff' : '#f9f9f9',
          border: isSelected ? '2px solid #1890ff' : '1px solid #e0e0e0',
          borderRadius: 6,
          padding: 10,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <Row justify="space-between" align="top">
          <Col flex="auto">
            <Space size={4} align="start">
              <HolderOutlined
                {...attributes}
                {...listeners}
                style={{ cursor: 'grab', color: '#999', fontSize: 12, marginTop: 2 }}
              />
              <div style={{ flex: 1 }}>
                <Space size={4}>
                  <span style={{ color: '#1890ff', fontSize: 13 }}>
                    {FIELD_ICONS[field.type]}
                  </span>
                  <Text strong style={{ fontSize: 12 }}>{field.label}</Text>
                  {field.required && <Tag color="red" style={{ fontSize: 9, padding: '0 3px' }}>*</Tag>}
                </Space>
                <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                  {fieldType?.label} • {widthPercentage}%
                </div>
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined style={{ fontSize: 12 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              style={{ padding: '0 4px', height: 20 }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default function FormBuilderPage() {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [columns, setColumns] = useState<2 | 3>(2);
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedField, setSelectedField] = useState<FormField | null>(null);
  const [ghostPosition, setGhostPosition] = useState<{ row: number; col: number } | null>(null);
  const [isTemplate, setIsTemplate] = useState(false);

  useEffect(() => {
    if (formId) fetchForm();
  }, [formId]);

  const fetchForm = async () => {
    try {
      const res = await fetch(`/api/v1/form-builder/forms/${formId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      setFormName(data.name);
      setFormDescription(data.description || '');
      setColumns(data.layout.columns);
      setFields(data.layout.fields || []);
      setIsTemplate(data.isTemplate || false);
    } catch (error) {
      message.error('Failed to load form');
    }
  };

  // Calculate next available position
  const getNextPosition = (colSpan: number = 1): { row: number; col: number } => {
    if (fields.length === 0) return { row: 0, col: 0 };

    // Build row map
    const rowMap: Map<number, number> = new Map();
    fields.forEach((f) => {
      const currentUsed = rowMap.get(f.position.row) || 0;
      rowMap.set(f.position.row, currentUsed + (f.position.colSpan || 1));
    });

    // Find first row with enough space
    let targetRow = 0;
    while (true) {
      const used = rowMap.get(targetRow) || 0;
      if (used + colSpan <= columns) {
        return { row: targetRow, col: used };
      }
      targetRow++;
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const fieldType = e.dataTransfer.getData('fieldType');
    if (!fieldType) return;

    const fieldDef = FIELD_TYPES.find((ft) => ft.type === fieldType);
    if (!fieldDef) return;

    const defaultColSpan = 1; // Default to 50% (1 out of 2 columns)
    const position = getNextPosition(defaultColSpan);

    const newField: FormField = {
      id: `field_${Date.now()}`,
      type: fieldDef.type,
      label: fieldDef.defaultLabel,
      required: false,
      position: { ...position, colSpan: defaultColSpan },
    };

    setFields([...fields, newField]);
    setSelectedField(newField);
    setGhostPosition(null);
    message.success(`${fieldDef.label} added`);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    
    // Show ghost at next position
    const nextPos = getNextPosition(1);
    setGhostPosition(nextPos);
  };

  const handleDragLeave = () => {
    setGhostPosition(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const newFields = arrayMove(
        fields,
        fields.findIndex((i) => i.id === active.id),
        fields.findIndex((i) => i.id === over.id)
      );
      
      // Reflow fields to update grid positions
      const reflowed = reflowFields(newFields, columns);
      setFields(reflowed);
    }
  };

  const handleSave = async (saveAsTemplate: boolean = false) => {
    if (!formName.trim()) {
      message.error('Please enter form name');
      return;
    }

    try {
      const url = formId ? `/api/v1/form-builder/forms/${formId}` : '/api/v1/form-builder/forms';
      const res = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: formName,
          description: formDescription,
          layout: { columns, fields },
          isTemplate: saveAsTemplate,
        }),
      });

      if (res.ok) {
        const action = formId ? 'updated' : 'created';
        const type = saveAsTemplate ? 'Template' : 'Form';
        message.success(`${type} ${action} successfully`);
        navigate(saveAsTemplate ? '/feedback/form-templates' : '/feedback/forms');
      }
    } catch (error) {
      message.error('Failed to save');
    }
  };

  const handleDeleteField = (fieldId: string) => {
    setFields(fields.filter((f) => f.id !== fieldId));
    if (selectedField?.id === fieldId) setSelectedField(null);
  };

  const handleUpdateField = (updates: Partial<FormField>) => {
    if (!selectedField) return;

    const updatedFields = fields.map((f) => {
      if (f.id === selectedField.id) {
        return { ...f, ...updates };
      }
      return f;
    });

    // Reflow fields if width changed
    if (updates.position?.colSpan !== undefined) {
      const reflow = reflowFields(updatedFields, columns);
      setFields(reflow);
      setSelectedField({ ...selectedField, ...updates });
    } else {
      setFields(updatedFields);
      setSelectedField({ ...selectedField, ...updates });
    }
  };

  // Reflow fields to fit grid
  const reflowFields = (fieldsList: FormField[], cols: number): FormField[] => {
    let currentRow = 0;
    let currentCol = 0;

    return fieldsList.map((field) => {
      const span = field.position.colSpan || 1;
      
      // Check if field fits in current row
      if (currentCol + span > cols) {
        currentRow++;
        currentCol = 0;
      }

      const newField = {
        ...field,
        position: {
          row: currentRow,
          col: currentCol,
          colSpan: span,
        },
      };

      currentCol += span;
      if (currentCol >= cols) {
        currentRow++;
        currentCol = 0;
      }

      return newField;
    });
  };

  // Organize fields into rows
  const renderFields = () => {
    const rows: FormField[][] = [];
    const rowMap = new Map<number, FormField[]>();

    fields.forEach((field) => {
      const row = field.position.row;
      if (!rowMap.has(row)) rowMap.set(row, []);
      rowMap.get(row)!.push(field);
    });

    const sortedRows = Array.from(rowMap.keys()).sort((a, b) => a - b);
    sortedRows.forEach((rowNum) => {
      rows.push(rowMap.get(rowNum)!.sort((a, b) => a.position.col - b.position.col));
    });

    return rows;
  };

  const fieldsByCategory = FIELD_TYPES.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = [];
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, typeof FIELD_TYPES>);

  const categoryLabels = {
    text: '📝 Text', number: '🔢 Numbers', datetime: '📅 Date/Time',
    choice: '☑️ Choice', special: '⭐ Special', location: '🌍 Location',
    display: '📋 Display',
  };

  const rows = renderFields();

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#f5f5f5', display: 'flex', flexDirection: 'column', zIndex: 1000,
    }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '12px 20px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/feedback/forms')}>Back</Button>
              <Title level={4} style={{ margin: 0 }}>{formId ? 'Edit Form' : 'Create New Form'}</Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Select value={columns} onChange={setColumns} style={{ width: 120 }}>
                <Select.Option value={2}>2 Columns</Select.Option>
                <Select.Option value={3}>3 Columns</Select.Option>
              </Select>
              {isTemplate ? (
                <>
                  <Button icon={<SaveOutlined />} onClick={() => handleSave(false)}>Save as Form</Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={() => handleSave(true)}>Update Template</Button>
                </>
              ) : (
                <>
                  <Button icon={<SaveOutlined />} onClick={() => handleSave(true)}>Save as Template</Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={() => handleSave(false)}>Save Form</Button>
                </>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      <Row style={{ flex: 1, overflow: 'hidden' }}>
        {/* LEFT */}
        <Col span={5} style={{ background: '#fff', borderRight: '1px solid #e8e8e8', height: '100%', overflowY: 'auto' }}>
          <div style={{ padding: '12px 14px' }}>
            <Title level={5} style={{ marginBottom: 4 }}>Field Types</Title>
            <Text type="secondary" style={{ fontSize: 11 }}>Drag to add</Text>
            <Divider style={{ margin: '12px 0' }} />

            {Object.entries(fieldsByCategory).map(([category, categoryFields]) => (
              <div key={category} style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
                  {categoryLabels[category as keyof typeof categoryLabels]}
                </Text>
                {categoryFields.map((field) => (
                  <DraggableFieldType key={field.type} fieldType={field} />
                ))}
              </div>
            ))}
          </div>
        </Col>

        {/* CENTER */}
        <Col
          span={12}
          style={{ background: '#fafafa', height: '100%', overflowY: 'auto' }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div style={{ padding: 20 }}>
            <Card>
              <Input
                placeholder="Form Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}
                bordered={false}
              />
              <Input.TextArea
                placeholder="Brief description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                autoSize={{ minRows: 1, maxRows: 2 }}
                bordered={false}
                style={{ fontSize: 13 }}
              />

              <Divider style={{ margin: '16px 0' }} />
              <Text strong style={{ fontSize: 14 }}>Form Preview ({fields.length} fields)</Text>

              <div style={{ marginTop: 16 }}>
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    {rows.map((row, rowIndex) => (
                      <div key={rowIndex} style={{ marginBottom: 0 }}>
                        {row.map((field) => (
                          <SortableFieldItem
                            key={field.id}
                            field={field}
                            isSelected={selectedField?.id === field.id}
                            onSelect={() => setSelectedField(field)}
                            onDelete={() => handleDeleteField(field.id)}
                            columns={columns}
                          />
                        ))}
                      </div>
                    ))}
                  </SortableContext>
                </DndContext>

                {/* Ghost placeholder */}
                {ghostPosition && (
                  <div style={{ 
                    width: `${100 / columns}%`, 
                    display: 'inline-block', 
                    padding: '0 4px',
                    marginBottom: 8,
                    boxSizing: 'border-box',
                  }}>
                    <div style={{
                      border: '2px dashed #1890ff',
                      borderRadius: 6,
                      padding: 10,
                      background: '#e6f7ff',
                      textAlign: 'center',
                      fontSize: 11,
                      color: '#1890ff',
                    }}>
                      Drop here
                    </div>
                  </div>
                )}

                {fields.length === 0 && !ghostPosition && (
                  <div style={{
                    textAlign: 'center', padding: 50, background: '#f9f9f9',
                    borderRadius: 6, border: '2px dashed #ccc',
                  }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Drag fields from left to add them
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </Col>

        {/* RIGHT */}
        <Col span={7} style={{ background: '#fff', borderLeft: '1px solid #e8e8e8', height: '100%', overflowY: 'auto' }}>
          <div style={{ padding: '12px 16px' }}>
            <Title level={5}>Field Properties</Title>
            <Divider style={{ margin: '12px 0' }} />

            {selectedField ? (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text strong style={{ fontSize: 12 }}>Type</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color="blue" icon={FIELD_ICONS[selectedField.type]}>
                      {FIELD_TYPES.find((ft) => ft.type === selectedField.type)?.label}
                    </Tag>
                  </div>
                </div>

                <div>
                  <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Label</Text>
                  <Input
                    value={selectedField.label}
                    onChange={(e) => handleUpdateField({ label: e.target.value })}
                  />
                </div>

                <div>
                  <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Placeholder</Text>
                  <Input
                    value={selectedField.placeholder}
                    onChange={(e) => handleUpdateField({ placeholder: e.target.value })}
                  />
                </div>

                <div>
                  <Row justify="space-between">
                    <Text strong style={{ fontSize: 12 }}>Required</Text>
                    <Switch
                      checked={selectedField.required}
                      onChange={(checked) => handleUpdateField({ required: checked })}
                    />
                  </Row>
                </div>

                <div>
                  <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Width</Text>
                  <Select
                    value={selectedField.position.colSpan || 1}
                    onChange={(value) =>
                      handleUpdateField({
                        position: { ...selectedField.position, colSpan: value },
                      })
                    }
                    style={{ width: '100%' }}
                  >
                    {Array.from({ length: columns }, (_, i) => i + 1).map(span => (
                      <Select.Option key={span} value={span}>
                        {Math.round((span / columns) * 100)}% ({span}/{columns})
                      </Select.Option>
                    ))}
                  </Select>
                  <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
                    Changing width will automatically reflow remaining fields
                  </Text>
                </div>

                {FIELD_TYPES.find((ft) => ft.type === selectedField.type)?.hasOptions && (
                  <div>
                    <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Options</Text>
                    <Input.TextArea
                      value={selectedField.options?.join('\n') || ''}
                      onChange={(e) =>
                        handleUpdateField({
                          options: e.target.value.split('\n').filter((o) => o.trim()),
                        })
                      }
                      rows={5}
                      placeholder="Option 1&#10;Option 2"
                    />
                  </div>
                )}
              </Space>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Select a field to edit</Text>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
}
